import { PDFDocument, PDFFont } from "pdf-lib"
import { embedFonts, TPdfFontName } from "./fontManager"
import { TPdfLayoutType } from "./types"

/**
 * iOS ViewFont.uifont fallback chain:
 *   "{Family}-{StylesJoined}" -> "{Family}-Regular" -> Lato-Regular
 * (e.g. BeausiteDetail-Bold does not exist and BeausiteDetail-Regular does not
 * exist either, so it falls all the way back to Lato-Regular — replicated here.)
 */

export type TFontFamily = "Lato" | "BeausiteText" | "BeausiteDetail" | "BentonSans"

// Fonts needed per layout type (resolver fallbacks included)
const layoutFonts: Record<TPdfLayoutType, TPdfFontName[]> = {
	"": ["Lato-Regular", "Lato-Bold", "Lato-Italic", "Lato-Medium", "Lato-Semibold", "BeausiteDetail-Clear"],
	wc: ["Lato-Regular", "Lato-Bold", "BeausiteText-Light", "BeausiteDetail-Clear", "BeausiteDetail-ClearItalic"],
	gould: ["Lato-Regular", "Lato-Bold", "BentonSans-Regular", "BeausiteDetail-Clear"],
}

export interface IFontSet {
	resolve(family: TFontFamily, stylesJoined: string): PDFFont
	coverFont(layoutType: TPdfLayoutType): PDFFont
	coverFontSize(layoutType: TPdfLayoutType): number
	contentFamily(layoutType: TPdfLayoutType): TFontFamily
}

export async function embedFontSet(doc: PDFDocument, layoutType: TPdfLayoutType): Promise<IFontSet> {
	const names = layoutFonts[layoutType] ?? layoutFonts[""]
	const fonts = await embedFonts(doc, names)
	const lookup = fonts as Partial<Record<TPdfFontName, PDFFont>>

	const resolve = (family: TFontFamily, stylesJoined: string): PDFFont => {
		const exact = lookup[`${family}-${stylesJoined}` as TPdfFontName]
		if (exact) {
			return exact
		}
		const regular = lookup[`${family}-Regular` as TPdfFontName]
		if (regular) {
			return regular
		}
		return lookup["Lato-Regular"] as PDFFont
	}

	return {
		resolve,
		// iOS PDFLayoutType.coverUIFont
		coverFont: (layout: TPdfLayoutType) => {
			if (layout === "gould") {
				return resolve("BentonSans", "Regular")
			}
			if (layout === "wc") {
				return resolve("BeausiteText", "Light")
			}
			return resolve("Lato", "Medium")
		},
		coverFontSize: (layout: TPdfLayoutType) => (layout === "wc" ? 30 : 20),
		// iOS PDFLayoutType.contentFont
		contentFamily: (layout: TPdfLayoutType) => {
			if (layout === "gould") {
				return "BentonSans"
			}
			if (layout === "wc") {
				return "BeausiteDetail"
			}
			return "Lato"
		},
	}
}
